/**
 * Export Utilities
 * Functions for exporting data to CSV, Excel, PNG, and PDF formats
 */

import * as XLSX from 'xlsx'
import html2canvas from 'html2canvas'
import { saveAs } from 'file-saver'
import { jsPDF } from 'jspdf'

/**
 * Generate filename with timestamp
 * @param prefix - Filename prefix (e.g., "EngagementHealth_Burndown")
 * @param engagementId - Engagement identifier
 * @param extension - File extension without dot (e.g., "csv", "xlsx", "png")
 * @returns Formatted filename
 */
export function generateFilename(prefix: string, engagementId: string | number, extension: string): string {
  const timestamp = new Date().toISOString().split('T')[0] // YYYY-MM-DD
  return `${prefix}_${engagementId}_${timestamp}.${extension}`
}

/**
 * Regex pattern to detect formula injection attempts
 * Matches values starting with: whitespace, =, +, -, @
 */
const FORMULA_PREFIX = /^[\s=+\-@]/

/**
 * Sanitize a value to prevent CSV/Excel formula injection
 * Prefixes dangerous characters with a single quote to force text interpretation
 * @param value - The value to sanitize
 * @returns Sanitized value safe for spreadsheet export
 */
const sanitizeForSpreadsheet = (value: unknown): unknown => {
  if (typeof value === 'string' && FORMULA_PREFIX.test(value)) {
    return `'${value}`
  }
  return value
}

/**
 * Sanitize all values in an array of objects
 * @param rows - Array of objects to sanitize
 * @returns Sanitized array safe for spreadsheet export
 */
const sanitizeRows = (rows: Record<string, unknown>[]) =>
  rows.map((row) =>
    Object.fromEntries(
      Object.entries(row).map(([key, value]) => [key, sanitizeForSpreadsheet(value)])
    )
  )

/**
 * Export data to CSV
 * @param data - Array of objects to export
 * @param filename - Output filename
 */
export function exportToCSV(data: any[], filename: string): void {
  const safeData = sanitizeRows(data as Record<string, unknown>[])
  const worksheet = XLSX.utils.json_to_sheet(safeData)
  const csv = XLSX.utils.sheet_to_csv(worksheet)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  saveAs(blob, filename)
}

/**
 * Export data to Excel
 * @param data - Array of objects to export
 * @param filename - Output filename
 * @param sheetName - Name of the worksheet (default: "Sheet1")
 */
export function exportToExcel(data: any[], filename: string, sheetName: string = 'Sheet1'): void {
  const safeData = sanitizeRows(data as Record<string, unknown>[])
  const worksheet = XLSX.utils.json_to_sheet(safeData)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  XLSX.writeFile(workbook, filename)
}

/** One exported row plus its depth in the outline. */
export interface OutlinedExportRow {
  data: Record<string, unknown>
  /** 0 = top level, 1 = a child of the nearest level-0 row above it. Defaults to 0. */
  level?: number
}

/**
 * Export to Excel with collapsible row groups, so a summary row can be expanded to reveal the rows
 * it summarises - the file then carries the same structure as the screen instead of flattening to
 * either the summary or the detail.
 *
 * Every row must carry the same keys in the same order: json_to_sheet takes the header from the
 * first object alone, so a child with a different shape would land its values under the wrong
 * headings rather than fail.
 */
export function exportToExcelOutlined(
  rows: OutlinedExportRow[],
  filename: string,
  sheetName: string = 'Sheet1'
): void {
  const safeData = sanitizeRows(rows.map((r) => r.data))
  const worksheet = XLSX.utils.json_to_sheet(safeData)

  // Row 0 is the header, which sits outside the outline; body rows follow in order. Children start
  // hidden so the file opens at the summary level and expands on demand, matching the screen.
  worksheet['!rows'] = [
    {},
    ...rows.map((r) => ({ level: r.level ?? 0, hidden: (r.level ?? 0) > 0 })),
  ]

  // Excel's default is summaryBelow, which puts the +/- toggle on the row AFTER each group - here
  // that is the next summary row, so every group would be expanded by its neighbour's control.
  // Our summary rows sit above their children, so the outline has to be told.
  worksheet['!outline'] = { above: true }

  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName)
  XLSX.writeFile(workbook, filename)
}

/**
 * Export HTML element to PNG
 * @param elementId - ID of the HTML element to capture
 * @param filename - Output filename
 */
export async function exportToPNG(elementId: string, filename: string): Promise<void> {
  const element = document.getElementById(elementId)
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`)
    return
  }

  try {
    const canvas = await html2canvas(element, {
      backgroundColor: '#ffffff',
      scale: 2, // Higher quality
      logging: false,
    })
    
    // Wrap toBlob in a Promise to properly await completion
    await new Promise<void>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to create PNG blob (canvas may be tainted)'))
          return
        }
        saveAs(blob, filename)
        resolve()
      })
    })
  } catch (error) {
    console.error('Failed to export PNG:', error)
  }
}

/**
 * Export HTML element to PDF
 * @param elementId - ID of the HTML element to capture
 * @param filename - Output filename (without extension)
 */
export async function exportToPDF(elementId: string, filename: string): Promise<void> {
  const element = document.getElementById(elementId)
  if (!element) {
    console.error(`Element with ID "${elementId}" not found`)
    return
  }

  try {
    // Capture the element as a canvas
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
    })

    // Calculate dimensions for A4 page
    const imgWidth = 210 // A4 width in mm
    const pageHeight = 297 // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width
    let heightLeft = imgHeight

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4')
    let position = 0

    // Add first page
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight)
    heightLeft -= pageHeight

    // Add additional pages if content is taller than one page
    while (heightLeft > 0) {
      position = heightLeft - imgHeight
      pdf.addPage()
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, position, imgWidth, imgHeight)
      heightLeft -= pageHeight
    }

    // Save the PDF
    pdf.save(filename)
  } catch (error) {
    console.error('Error exporting PDF:', error)
  }
}

